/**
 * Breadcrumb — fil d'Ariane standardisé du profil Particulier.
 *
 * Préfixé par défaut par "Accueil / Particuliers". Le dernier item est
 * non-cliquable et mis en bold (page courante).
 *
 * À utiliser systématiquement en haut du Container du hero des pages
 * de l'espace /particuliers/*.
 *
 * Pour les pages d'autres audiences qui réutilisent ce composant
 * (ex. /agriculteurs/*), passer un `prefix` custom pour remplacer le
 * préfixe par défaut.
 */

import { cn } from '../../../lib/cn';

export interface ParticulierBreadcrumbProps {
  /**
   * Liste des items SANS le préfixe (préfixé automatiquement). Le dernier
   * item est traité comme la page courante.
   * Exemple : [{ name: 'Aides', href: '/particuliers/aides' }, { name: 'MaPrimeRénov’' }]
   */
  trail: Array<{ name: string; href?: string }>;
  /**
   * Préfixe du fil d'Ariane. Par défaut : "Accueil / Particuliers".
   * Override possible pour les pages d'autres audiences (ex. Agriculteurs).
   * Passer un tableau vide `[]` pour désactiver le préfixe entièrement.
   */
  prefix?: Array<{ name: string; href?: string }>;
  /** Pour overrider la position relative par rapport à la marge */
  className?: string;
}

const DEFAULT_PREFIX: Array<{ name: string; href?: string }> = [
  { name: 'Accueil', href: '/' },
  { name: 'Particuliers', href: '/particuliers' },
];

export function ParticulierBreadcrumb({ trail, prefix, className }: ParticulierBreadcrumbProps) {
  const allItems: Array<{ name: string; href?: string }> = [
    ...(prefix ?? DEFAULT_PREFIX),
    ...trail,
  ];
  const lastIndex = allItems.length - 1;

  return (
    <nav
      aria-label="Fil d&rsquo;Ariane"
      className={cn('mb-8 text-[12px] text-stone-500', className)}
    >
      {allItems.map((item, idx) => {
        const isLast = idx === lastIndex;
        return (
          <span key={`${item.name}-${idx}`}>
            {item.href && !isLast ? (
              <a href={item.href} className="hover:text-brand-700">
                {item.name}
              </a>
            ) : (
              <span className={isLast ? 'font-medium text-stone-700' : ''}>{item.name}</span>
            )}
            {!isLast && <span className="mx-1.5 text-stone-300">/</span>}
          </span>
        );
      })}
    </nav>
  );
}
